import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import {
  Callout,
  Empty,
  EmptyState,
} from '../../../components/common/detailListParts';
import {
  CopyableValue,
  Disclosure,
  Field,
  FieldGrid,
  Section,
} from '../../../components/common/detailParts';
import { TypedStatusBadge } from '../../../components/common/StatusBadge';
import { sumsubApplicantUrl } from '../publisherSignals';
import type { SuperPublisherTabProps } from '../types';

function SumsubLink({ applicantId }: { applicantId: string }) {
  return (
    <a
      href={sumsubApplicantUrl(applicantId)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-w-0 items-center gap-1 font-mono text-teal-400 transition-colors hover:text-teal-300"
    >
      <span className="truncate">{applicantId}</span>
      <OpenInNewIcon className="size-3 shrink-0 fill-current" />
    </a>
  );
}

export function KybTab({ data, formatDate }: SuperPublisherTabProps) {
  const { kybSubmission } = data.publisher;

  if (!kybSubmission) {
    return (
      <Section title="KYB / KYC">
        <EmptyState message="No verification has been started for this publisher" />
      </Section>
    );
  }

  const applicant = kybSubmission.sumsubApplicant;

  return (
    <div className="space-y-4">
      <Section
        title="Submission"
        trailing={<TypedStatusBadge type="kyb" status={kybSubmission.status} />}
      >
        <FieldGrid>
          <Field label="Submission ID">
            <CopyableValue value={kybSubmission.id} />
          </Field>
          <Field label="Sumsub applicant">
            {kybSubmission.sumsubApplicantId ? (
              <SumsubLink applicantId={kybSubmission.sumsubApplicantId} />
            ) : (
              <Empty />
            )}
          </Field>
          <Field label="Submitted">
            <span className="text-neutral-300">
              {formatDate(kybSubmission.submittedAt)}
            </span>
          </Field>
          <Field label="Review started">
            <span className="text-neutral-300">
              {formatDate(kybSubmission.reviewStartedAt)}
            </span>
          </Field>
          <Field label="Review completed">
            <span className="text-neutral-300">
              {formatDate(kybSubmission.reviewCompletedAt)}
            </span>
          </Field>
        </FieldGrid>

        {(kybSubmission.rejectionReason ||
          kybSubmission.additionalInfoRequested) && (
          <div className="mt-5 flex flex-col gap-2">
            {kybSubmission.rejectionReason && (
              <Callout label="Rejected:">
                {kybSubmission.rejectionReason}
              </Callout>
            )}
            {kybSubmission.additionalInfoRequested && (
              <Callout tone="amber" label="Info requested:">
                {kybSubmission.additionalInfoRequested}
              </Callout>
            )}
          </div>
        )}
      </Section>

      {applicant && (
        <Section title="Sumsub applicant">
          <FieldGrid>
            <Field label="Sumsub ID">
              <SumsubLink applicantId={applicant.sumsubId} />
            </Field>
            <Field label="Review status">
              <span className="text-neutral-300">
                {applicant.reviewStatus ?? '—'}
              </span>
            </Field>
            <Field label="Company name">
              <span className="text-neutral-300">
                {applicant.companyName ?? '—'}
              </span>
            </Field>
            <Field label="Last synced">
              <span className="text-neutral-300">
                {formatDate(applicant.lastSyncedAt)}
              </span>
            </Field>
          </FieldGrid>

          {applicant.reviewResult !== null && (
            <Disclosure label="Review result">
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-white/5 bg-black/20 p-3 text-xs leading-relaxed text-neutral-300">
                {JSON.stringify(applicant.reviewResult, null, 2)}
              </pre>
            </Disclosure>
          )}
        </Section>
      )}
    </div>
  );
}
